import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Target, Zap, Users, Shield, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Clock, ArrowRight, Loader as Loader2, RefreshCw, ChevronDown, ChevronRight, Sparkles, SlidersHorizontal, Search, Info } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import { DRIVER_FIELD_GROUPS } from '../../lib/executive-analytics/fieldSeeds';
import FieldCard from './FieldCard';
import type { Account } from '../../types';
import type {
  EAWorkspace, EAInputField, FieldCategory,
  ValueDriverType, EAWorkspaceValueDriver,
} from '../../types/executiveAnalytics';
import { VALUE_DRIVER_LABELS } from '../../types/executiveAnalytics';

interface ConfigureTabProps {
  account: Account;
  workspace: EAWorkspace;
  onWorkspaceUpdate: () => Promise<void>;
  onProceedToAnalytics: () => void;
}

interface IntelligenceSummary {
  valuePyramids: number;
  valueStatements: number;
  useCases: number;
  triggers: number;
  incumbents: number;
}

interface ValidationResult {
  completeness: number;
  confidence: number;
  warnings: string[];
  errors: string[];
  unvalidatedResearchCount: number;
  isValid: boolean;
}

const COMMON_CATEGORIES: FieldCategory[] = ['company', 'financial', 'vendor', 'timeline', 'operational'];

const DRIVER_ICONS: Record<ValueDriverType, string> = {
  revenue_growth: 'text-app-focus bg-app-focus/10 border-app-focus/30',
  cost_efficiency: 'text-app-focus bg-app-focus/10 border-app-focus',
  risk_reduction: 'text-app-critical bg-app-critical/10 border-app-critical',
  compliance_regulatory: 'text-app-warning bg-app-warning/10 border-app-warning',
  productivity_workforce: 'text-app-focus bg-app-focus/10 border-app-focus/30',
  infrastructure_performance: 'text-app-muted bg-app-canvas border-app-line',
  transformation_strategic: 'text-app-positive bg-app-positive/10 border-app-positive',
};

const COST_SAVINGS_TYPE_OPTIONS = [
  { value: 'efficiency_reallocation', label: 'Efficiency (cost avoided / optimised)' },
  { value: 'hard_cost_takeout', label: 'Hard Cost Removal (budget eliminated)' },
];

export default function ConfigureTab({
  account, workspace, onWorkspaceUpdate, onProceedToAnalytics,
}: ConfigureTabProps) {
  const { apiGet, apiPost, apiPut } = useApi();
  const [intelligence, setIntelligence] = useState<IntelligenceSummary | null>(null);
  const [inputFields, setInputFields] = useState<EAInputField[]>([]);
  const [valueDrivers, setValueDrivers] = useState<EAWorkspaceValueDriver[]>([]);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [researching, setResearching] = useState(false);
  const [validating, setValidating] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [seedError, setSeedError] = useState('');
  const [researchError, setResearchError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const activeDriverTypes = valueDrivers
    .filter(d => d.is_user_enabled)
    .map(d => d.driver_type);

  useEffect(() => {
    loadData();
  }, [workspace.id, account.id]);

  // Single AWS read backing the Configure tab: intelligence counts + input
  // fields + value drivers. The GET find-or-seeds the input fields server-side
  // (port of the legacy `seedWorkspaceFields`), so no client seed remains.
  const fetchConfigure = () =>
    apiGet<{
      intelligence: IntelligenceSummary;
      input_fields: EAInputField[];
      value_drivers: EAWorkspaceValueDriver[];
    }>(`/v1/ea/workspace/${workspace.id}/configure`);

  const loadData = async () => {
    setLoading(true);
    setSeedError('');
    try {
      const data = await fetchConfigure();
      setIntelligence(data.intelligence);
      setInputFields(data.input_fields || []);
      setValueDrivers(data.value_drivers || []);
    } catch (err) {
      setSeedError(err instanceof Error ? err.message : 'Failed to configure workspace');
    } finally {
      setLoading(false);
    }
  };

  const loadValueDrivers = async () => {
    const data = await fetchConfigure();
    setValueDrivers(data.value_drivers || []);
  };

  const reloadFields = async () => {
    const data = await fetchConfigure();
    if (data.input_fields) setInputFields(data.input_fields);
  };

  const runDriverDetection = async () => {
    setDetecting(true);
    try {
      await apiPost(`/v1/ea/workspace/${workspace.id}/detect-value-drivers`, { workspace_id: workspace.id });
      await Promise.all([loadValueDrivers(), reloadFields(), onWorkspaceUpdate()]);
    } catch {
      setSeedError('Value driver detection failed. Please retry or toggle the value drivers you need below.');
    } finally {
      setDetecting(false);
    }
  };

  useEffect(() => {
    if (
      !loading && !detecting &&
      inputFields.length > 0 &&
      valueDrivers.length === 0 &&
      workspace.driver_detection_status === 'none'
    ) {
      runDriverDetection();
    }
  }, [loading, inputFields.length, valueDrivers.length, workspace.driver_detection_status]);

  const toggleDriver = async (driverId: string, currentEnabled: boolean) => {
    await apiPut(`/v1/ea/value-drivers/${driverId}`, { is_user_enabled: !currentEnabled });
    setValueDrivers(prev => prev.map(d =>
      d.id === driverId ? { ...d, is_user_enabled: !currentEnabled } : d
    ));
  };

  const isFieldVisible = useCallback((field: EAInputField): boolean => {
    const fieldDrivers = (field.value_drivers || []) as ValueDriverType[];
    if (fieldDrivers.length === 0) return true;
    if (activeDriverTypes.length === 0) return true;
    return fieldDrivers.some(d => activeDriverTypes.includes(d));
  }, [activeDriverTypes]);

  const visibleFields = inputFields.filter(isFieldVisible);

  useEffect(() => {
    if (visibleFields.length > 0) {
      calculateValidation(visibleFields);
    }
  }, [visibleFields.length, activeDriverTypes.length, inputFields]);

  const calculateValidation = (fields: EAInputField[]) => {
    const required = fields.filter(f => f.is_required);
    const filled = required.filter(f => f.value && f.value.trim() !== '');
    const completeness = required.length > 0 ? (filled.length / required.length) * 100 : 100;

    const withConf = fields.filter(f => f.value && f.confidence_score > 0);
    const avgConf = withConf.length > 0
      ? withConf.reduce((s, f) => s + f.confidence_score, 0) / withConf.length
      : 0;

    const warnings: string[] = [];
    const errors: string[] = [];

    const unvalidatedResearch = fields.filter(
      f => f.is_research_populated && !f.user_validated && f.value && f.value.trim() !== ''
    );

    fields.forEach(f => {
      if (f.is_required && (!f.value || f.value.trim() === '')) {
        errors.push(`Missing required: ${f.label}`);
      }
      if (f.value && f.classification === 'modelled' && f.confidence_score < 0.7) {
        warnings.push(`Low confidence: ${f.label} (${Math.round(f.confidence_score * 100)}%)`);
      }
    });

    if (unvalidatedResearch.length > 0) {
      warnings.push(`${unvalidatedResearch.length} researched field${unvalidatedResearch.length > 1 ? 's' : ''} still need${unvalidatedResearch.length === 1 ? 's' : ''} review`);
    }

    const canValidate = errors.length === 0 && completeness >= 80 && unvalidatedResearch.length === 0;

    setValidation({
      completeness,
      confidence: avgConf * 100,
      warnings,
      errors,
      unvalidatedResearchCount: unvalidatedResearch.length,
      isValid: canValidate,
    });
  };

  const handleFieldSave = async (field: EAInputField) => {
    const newValue = editValues[field.id] ?? field.value;
    await apiPut(`/v1/ea/input-fields/${field.id}`, {
      value: newValue,
      source_type: 'user_input',
      classification: 'verified',
      confidence_score: 1.0,
      user_validated: true,
    });
    setEditingField(null);
    await reloadFields();
  };

  const handleValidateField = async (field: EAInputField) => {
    await apiPut(`/v1/ea/input-fields/${field.id}`, { user_validated: true });
    setInputFields(prev => prev.map(f =>
      f.id === field.id ? { ...f, user_validated: true } : f
    ));
  };

  const handleValidateAllResearched = async () => {
    const unvalidated = inputFields.filter(f => f.is_research_populated && !f.user_validated && f.value);
    if (unvalidated.length === 0) return;
    const ids = unvalidated.map(f => f.id);
    await apiPost(`/v1/ea/workspace/${workspace.id}/input-fields/validate-all`, {});
    setInputFields(prev => prev.map(f =>
      ids.includes(f.id) ? { ...f, user_validated: true } : f
    ));
  };

  const handleValidate = async () => {
    setValidating(true);
    const status = validation?.isValid ? 'validated' : 'in_progress';
    await apiPut(`/v1/ea/workspace/${workspace.id}/configure-status`, {
      configure_status: status,
      status: status === 'validated' ? 'analytics_ready' : 'configure_in_progress',
    });
    await onWorkspaceUpdate();
    setValidating(false);
  };

  const runDeepResearch = async () => {
    setResearching(true);
    setResearchError('');
    try {
      await apiPost(`/v1/ea/workspace/${workspace.id}/research-fields`, { workspace_id: workspace.id });
      await Promise.all([reloadFields(), onWorkspaceUpdate()]);
    } catch (err) {
      setResearchError(err instanceof Error ? err.message : 'Deep research failed');
    } finally {
      setResearching(false);
    }
  };

  const ADVANCED_KEYS = new Set(['relevant_cost_base_percent', 'cost_savings_type', 'realisation_factor', 'realisation_factor_labour', 'risk_realisation_factor']);

  const buildFieldGroups = () => {
    const groups: { key: string; label: string; fields: EAInputField[]; driverType?: ValueDriverType }[] = [];

    const commonFields = visibleFields.filter(f => COMMON_CATEGORIES.includes(f.category) && !ADVANCED_KEYS.has(f.field_key));
    const commonByCategory = COMMON_CATEGORIES.reduce((acc, cat) => {
      const items = commonFields.filter(f => f.category === cat);
      if (items.length > 0) {
        const labels: Record<string, string> = {
          company: 'Company Information',
          financial: 'Financial Data',
          vendor: 'Vendor / Product',
          timeline: 'Timeline & Milestones',
          operational: 'Operational Metrics',
        };
        acc.push({ key: `common_${cat}`, label: labels[cat] || cat, fields: items });
      }
      return acc;
    }, [] as { key: string; label: string; fields: EAInputField[] }[]);

    groups.push(...commonByCategory);

    const sortedDrivers = [...valueDrivers]
      .filter(d => d.is_user_enabled)
      .sort((a, b) => {
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        return a.sort_order - b.sort_order;
      });

    for (const driver of sortedDrivers) {
      const driverFieldKeys = DRIVER_FIELD_GROUPS[driver.driver_type] || [];
      const driverFields = visibleFields.filter(f => {
        if (ADVANCED_KEYS.has(f.field_key)) return false;
        const fd = (f.value_drivers || []) as ValueDriverType[];
        return fd.includes(driver.driver_type) && driverFieldKeys.includes(f.field_key);
      });

      const alreadyShownKeys = new Set(groups.flatMap(g => g.fields.map(f => f.field_key)));
      const uniqueFields = driverFields.filter(f => !alreadyShownKeys.has(f.field_key));

      if (uniqueFields.length > 0) {
        groups.push({
          key: `driver_${driver.driver_type}`,
          label: `${VALUE_DRIVER_LABELS[driver.driver_type]} Inputs`,
          fields: uniqueFields,
          driverType: driver.driver_type,
        });
      }
    }

    if (activeDriverTypes.length === 0) {
      const driverFields = visibleFields.filter(f => !COMMON_CATEGORIES.includes(f.category) && !ADVANCED_KEYS.has(f.field_key));
      const alreadyShownKeys = new Set(groups.flatMap(g => g.fields.map(f => f.field_key)));
      const remaining = driverFields.filter(f => !alreadyShownKeys.has(f.field_key));
      if (remaining.length > 0) {
        groups.push({ key: 'impact', label: 'Impact Inputs', fields: remaining });
      }
    }

    return groups;
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-app-brand animate-spin mx-auto mb-3" />
          <p className="text-sm text-app-muted">Configuring workspace fields...</p>
        </div>
      </div>
    );
  }

  const fieldGroups = buildFieldGroups();
  const researchedCount = inputFields.filter(f => f.is_research_populated).length;
  const unvalidatedResearchCount = inputFields.filter(f => f.is_research_populated && !f.user_validated && f.value).length;
  const emptyEditableCount = inputFields.filter(f => f.is_editable && (!f.value || f.value.trim() === '') && f.source_type !== 'user_input').length;
  const hasResearchRun = workspace.research_status === 'completed' || researchedCount > 0;

  return (
    <div className="space-y-6">
      <IntelligenceSummarySection intelligence={intelligence} />

      {detecting && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-app-brand animate-pulse" />
            <div>
              <p className="text-sm font-medium text-app-ink">Analysing deal context...</p>
              <p className="text-xs text-app-muted mt-0.5">Evaluating vendor solution, industry vertical, and account intelligence to determine relevant value drivers</p>
            </div>
          </div>
        </div>
      )}

      {valueDrivers.length > 0 && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-app-ink">Detected Value Drivers</h2>
            <button
              onClick={runDriverDetection}
              disabled={detecting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-app-muted bg-app-canvas rounded-lg hover:bg-app-line transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${detecting ? 'animate-spin' : ''}`} />
              Re-analyse
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {valueDrivers
              .sort((a, b) => a.sort_order - b.sort_order)
              .map(driver => {
                const colorClasses = DRIVER_ICONS[driver.driver_type] || 'text-app-muted bg-app-canvas border-app-line';
                return (
                  <button
                    key={driver.id}
                    onClick={() => toggleDriver(driver.id, driver.is_user_enabled)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition ${
                      driver.is_user_enabled
                        ? `${colorClasses} ring-1 ring-offset-1 ring-current/20`
                        : 'text-app-muted bg-app-canvas border-app-line opacity-60'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded flex items-center justify-center text-xs ${
                      driver.is_user_enabled ? 'bg-current/10' : 'bg-app-line'
                    }`}>
                      {driver.is_user_enabled ? '\u2713' : ''}
                    </span>
                    <span>{VALUE_DRIVER_LABELS[driver.driver_type]}</span>
                    {driver.is_primary && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-app-surface/50">PRIMARY</span>
                    )}
                    <span className="text-[10px] opacity-70">{Math.round(driver.confidence_score * 100)}%</span>
                  </button>
                );
              })}
          </div>
          {workspace.value_driver_context?.deal_narrative && (
            <p className="mt-3 text-xs text-app-muted italic">
              {workspace.value_driver_context.deal_narrative}
            </p>
          )}
        </div>
      )}

      <DeepResearchSection
        account={account}
        researching={researching}
        hasResearchRun={hasResearchRun}
        researchedCount={researchedCount}
        emptyEditableCount={emptyEditableCount}
        researchError={researchError}
        onRunResearch={runDeepResearch}
      />

      {unvalidatedResearchCount > 0 && (
        <div className="bg-app-warning/10 border border-app-warning rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-app-warning flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-app-warning">
                {unvalidatedResearchCount} researched value{unvalidatedResearchCount > 1 ? 's' : ''} awaiting review
              </p>
              <p className="text-xs text-app-warning mt-0.5">
                Accept each value individually, edit to override, or accept all at once below.
              </p>
            </div>
          </div>
          <button
            onClick={handleValidateAllResearched}
            className="flex items-center gap-1.5 px-4 py-2 bg-app-warning text-white rounded-lg text-sm font-medium hover:bg-app-warning transition-colors flex-shrink-0"
          >
            <CheckCircle className="w-4 h-4" />
            Accept All ({unvalidatedResearchCount})
          </button>
        </div>
      )}

      <div className="bg-app-surface rounded-lg border border-app-line p-6">
        <h2 className="text-lg font-semibold text-app-ink mb-1">Input Fields</h2>
        <p className="text-xs text-app-muted mb-4">
          {activeDriverTypes.length > 0
            ? `Showing ${visibleFields.length} fields relevant to your active value drivers. Toggle drivers above to adjust.`
            : `Showing all ${visibleFields.length} input fields.`
          }
          {' '}Click the info icon on any field to see what it is, why it matters, and how to ask about it.
        </p>

        {seedError && (
          <div className="mb-4 bg-app-critical/10 border border-app-critical rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-app-critical mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-app-critical">Configuration issue</p>
              <p className="text-sm text-app-critical mt-1">{seedError}</p>
            </div>
            <button
              onClick={() => { setSeedError(''); runDriverDetection(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-app-critical/10 text-app-critical rounded-lg text-sm font-medium hover:bg-app-critical transition-colors flex-shrink-0"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry Detection
            </button>
          </div>
        )}

        {fieldGroups.length === 0 && !seedError ? (
          <div className="text-center py-8">
            <AlertCircle className="w-8 h-8 text-app-line-strong mx-auto mb-3" />
            <p className="text-sm text-app-muted mb-4">No input fields have been configured yet.</p>
            <button
              onClick={() => { setSeedError(''); loadData(); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-app-brand text-white rounded-lg text-sm font-medium hover:bg-app-brand transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Configure Fields
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {fieldGroups.map(group => {
              const isExpanded = expandedGroups[group.key] !== false;
              const filledCount = group.fields.filter(f => f.value && f.value.trim() !== '').length;
              const groupUnvalidated = group.fields.filter(f => f.is_research_populated && !f.user_validated && f.value).length;
              return (
                <div key={group.key} className="border border-app-line rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-app-canvas hover:bg-app-canvas transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-app-muted" /> : <ChevronRight className="w-4 h-4 text-app-muted" />}
                      <span className="text-sm font-semibold text-app-ink">{group.label}</span>
                      {group.driverType && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${DRIVER_ICONS[group.driverType]}`}>
                          {VALUE_DRIVER_LABELS[group.driverType]}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {groupUnvalidated > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-app-warning/10 text-app-warning border border-app-warning">
                          {groupUnvalidated} to review
                        </span>
                      )}
                      <span className="text-xs text-app-muted">{filledCount}/{group.fields.length} populated</span>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {group.fields.map(field => (
                        <FieldCard
                          key={field.id}
                          field={field}
                          isEditing={editingField === field.id}
                          editValue={editValues[field.id] ?? field.value ?? ''}
                          onEdit={(id, val) => {
                            setEditingField(id);
                            setEditValues(prev => ({ ...prev, [id]: val }));
                          }}
                          onSave={handleFieldSave}
                          onValidateField={handleValidateField}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AdvancedAssumptions
        fields={inputFields}
        editingField={editingField}
        editValues={editValues}
        onEdit={(id: string, val: string) => {
          setEditingField(id);
          setEditValues(prev => ({ ...prev, [id]: val }));
        }}
        onSave={handleFieldSave}
        onValidateField={handleValidateField}
      />

      {validation && (
        <ValidationPanel
          validation={validation}
          onValidate={handleValidate}
          validating={validating}
        />
      )}

      <div className="flex justify-end">
        <button
          onClick={onProceedToAnalytics}
          disabled={workspace.configure_status !== 'validated'}
          className="flex items-center gap-2 px-6 py-3 bg-app-brand text-white rounded-lg font-medium hover:bg-app-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Proceed to Analytics
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function DeepResearchSection({
  account,
  researching,
  hasResearchRun,
  researchedCount,
  emptyEditableCount,
  researchError,
  onRunResearch,
}: {
  account: Account;
  researching: boolean;
  hasResearchRun: boolean;
  researchedCount: number;
  emptyEditableCount: number;
  researchError: string;
  onRunResearch: () => void;
}) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="bg-app-surface rounded-lg border border-app-line p-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-app-focus/10 border border-app-focus/30 flex items-center justify-center flex-shrink-0">
          <Search className="w-5 h-5 text-app-focus" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold text-app-ink">Deep Research</h2>
            <button
              onClick={() => setShowInfo(v => !v)}
              className={`p-1 rounded transition-colors ${showInfo ? 'bg-app-focus/10 text-app-focus' : 'text-app-muted hover:bg-app-canvas'}`}
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-app-muted">
            {hasResearchRun
              ? `Research populated ${researchedCount} fields with estimated values based on ${account.company_name}'s profile.`
              : `Research and estimate realistic values for the ${emptyEditableCount} empty fields using ${account.company_name}'s company profile, industry benchmarks and market data.`
            }
          </p>

          {showInfo && (
            <div className="mt-3 p-4 bg-app-focus/10 border border-app-focus rounded-lg space-y-2.5">
              <p className="text-xs text-app-focus leading-relaxed">
                Deep Research analyses public evidence about this company, its industry, market benchmarks and competitive landscape to estimate realistic values for each input field.
              </p>
              <p className="text-xs text-app-focus leading-relaxed">
                These are informed estimates based on the company's profile and should be treated as starting assumptions. Share the figures with the customer and invite them to challenge or refine the numbers. This is a collaborative starting point, not a final answer.
              </p>
              <p className="text-xs text-app-focus leading-relaxed font-medium">
                All researched values are clearly marked and must be reviewed before proceeding to analytics.
              </p>
            </div>
          )}

          {researchError && (
            <div className="mt-3 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
              <p className="text-sm text-app-critical">{researchError}</p>
            </div>
          )}

          {hasResearchRun && researchedCount > 0 && (
            <div className="mt-3 p-3 bg-app-focus/10 border border-app-focus/30 rounded-lg">
              <p className="text-xs text-app-focus">
                Research populated {researchedCount} fields. Review each value below, then accept it or select the value to edit and override it.
              </p>
            </div>
          )}
        </div>
        <button
          onClick={onRunResearch}
          disabled={researching || emptyEditableCount === 0}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
            researching
              ? 'bg-app-focus/10 text-app-focus cursor-wait'
              : emptyEditableCount === 0
              ? 'bg-app-canvas text-app-muted cursor-not-allowed'
              : 'bg-app-focus text-white hover:bg-app-focus'
          }`}
        >
          {researching ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Researching {account.company_name}...
            </>
          ) : hasResearchRun ? (
            <>
              <RefreshCw className="w-4 h-4" />
              Re-run Research
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              Run Deep Research
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function IntelligenceSummarySection({ intelligence }: { intelligence: IntelligenceSummary | null }) {
  if (!intelligence) return null;
  const cards = [
    { icon: TrendingUp, label: 'Value Pyramids', count: intelligence.valuePyramids, color: 'text-app-focus' },
    { icon: Target, label: 'Value Statements', count: intelligence.valueStatements, color: 'text-app-focus' },
    { icon: Shield, label: 'Use Cases', count: intelligence.useCases, color: 'text-app-positive' },
    { icon: Zap, label: 'Triggers', count: intelligence.triggers, color: 'text-app-warning' },
    { icon: Users, label: 'Incumbents', count: intelligence.incumbents, color: 'text-app-critical' },
  ];
  return (
    <div className="bg-app-surface rounded-lg border border-app-line p-6">
      <h2 className="text-lg font-semibold text-app-ink mb-4">Account Intelligence Summary</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map(({ icon: Icon, label, count, color }) => (
          <div key={label} className="bg-app-canvas rounded-lg p-4 border border-app-line">
            <div className="flex items-start justify-between mb-2">
              <Icon className={`w-5 h-5 ${color}`} />
              {count > 0 ? <CheckCircle className="w-4 h-4 text-app-positive" /> : <AlertCircle className="w-4 h-4 text-app-muted" />}
            </div>
            <div className="text-2xl font-bold text-app-ink mb-1">{count}</div>
            <div className="text-xs text-app-muted">{label}</div>
            <span className={`mt-2 inline-block px-2 py-0.5 rounded text-xs font-medium ${count > 0 ? 'bg-app-positive/10 text-app-positive' : 'bg-app-canvas text-app-muted'}`}>
              {count > 0 ? 'Available' : 'Missing'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdvancedAssumptions({
  fields, editingField, editValues, onEdit, onSave, onValidateField,
}: {
  fields: EAInputField[];
  editingField: string | null;
  editValues: Record<string, string>;
  onEdit: (id: string, val: string) => void;
  onSave: (f: EAInputField) => void;
  onValidateField: (f: EAInputField) => void;
}) {
  const [open, setOpen] = useState(false);
  const ADVANCED_FIELD_KEYS = new Set([
    'relevant_cost_base_percent', 'cost_savings_type', 'realisation_factor',
    'realisation_factor_labour', 'risk_realisation_factor',
  ]);
  const advancedFields = fields.filter(f => ADVANCED_FIELD_KEYS.has(f.field_key));
  if (advancedFields.length === 0) return null;

  const costScoping = advancedFields.filter(f => f.field_key === 'relevant_cost_base_percent' || f.field_key === 'cost_savings_type');
  const realisationFactors = advancedFields.filter(f => f.field_key.startsWith('realisation_factor') || f.field_key === 'risk_realisation_factor');

  return (
    <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-app-canvas transition-colors"
      >
        <div className="flex items-center gap-3">
          <SlidersHorizontal className="w-5 h-5 text-app-muted" />
          <div className="text-left">
            <h2 className="text-sm font-semibold text-app-ink">Advanced Assumptions</h2>
            <p className="text-xs text-app-muted">Cost scoping, realisation factors, and model controls</p>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-app-muted" /> : <ChevronRight className="w-4 h-4 text-app-muted" />}
      </button>
      {open && (
        <div className="px-6 pb-6 space-y-5 border-t border-app-line pt-4">
          {costScoping.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-3">Cost Scoping</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {costScoping.map(field => (
                  field.field_key === 'cost_savings_type' ? (
                    <CostSavingsTypeField key={field.id} field={field} onSave={onSave} />
                  ) : (
                    <FieldCard
                      key={field.id}
                      field={field}
                      isEditing={editingField === field.id}
                      editValue={editValues[field.id] ?? field.value ?? ''}
                      onEdit={onEdit}
                      onSave={onSave}
                      onValidateField={onValidateField}
                    />
                  )
                ))}
              </div>
            </div>
          )}
          {realisationFactors.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-3">Realisation Factors</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {realisationFactors.map(field => (
                  <FieldCard
                    key={field.id}
                    field={field}
                    isEditing={editingField === field.id}
                    editValue={editValues[field.id] ?? field.value ?? ''}
                    onEdit={onEdit}
                    onSave={onSave}
                    onValidateField={onValidateField}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CostSavingsTypeField({ field, onSave }: { field: EAInputField; onSave: (f: EAInputField) => void }) {
  const { apiPut } = useApi();
  const current = field.value || 'efficiency_reallocation';

  const handleChange = async (val: string) => {
    await apiPut(`/v1/ea/input-fields/${field.id}`, {
      value: val,
      source_type: 'user_input',
      classification: 'verified',
      confidence_score: 1.0,
      user_validated: true,
    });
    onSave({ ...field, value: val });
  };

  return (
    <div className="bg-app-canvas rounded-lg p-3 border border-app-line">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-app-ink">{field.label}</span>
      </div>
      <p className="text-xs text-app-muted mb-2">Hard cost removal requires decommissioning tools or reducing spend. Efficiency reflects avoided or optimised cost.</p>
      <div className="space-y-1.5">
        {COST_SAVINGS_TYPE_OPTIONS.map(opt => (
          <label key={opt.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition text-sm ${current === opt.value ? 'border-app-brand/20 bg-app-brand/10 text-app-brand' : 'border-app-line bg-app-surface text-app-ink hover:border-app-line-strong'}`}>
            <input
              type="radio"
              name="cost_savings_type"
              value={opt.value}
              checked={current === opt.value}
              onChange={() => handleChange(opt.value)}
              className="sr-only"
            />
            <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${current === opt.value ? 'border-app-brand/20 bg-app-brand' : 'border-app-line-strong'}`}>
              {current === opt.value && <span className="block w-full h-full rounded-full bg-app-surface scale-[0.35]" />}
            </span>
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function ValidationPanel({ validation, onValidate, validating }: {
  validation: ValidationResult;
  onValidate: () => void;
  validating: boolean;
}) {
  return (
    <div className="bg-app-surface rounded-lg border border-app-line p-6">
      <h2 className="text-lg font-semibold text-app-ink mb-4">Validation</h2>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-app-ink">Completeness</span>
            <span className="text-sm font-semibold text-app-ink">{Math.round(validation.completeness)}%</span>
          </div>
          <div className="w-full bg-app-line rounded-full h-2" role="progressbar" aria-label="Configuration completeness" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(validation.completeness)}>
            <div className="bg-app-brand h-2 rounded-full transition" style={{ width: `${validation.completeness}%` }} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-app-ink">Confidence</span>
            <span className="text-sm font-semibold text-app-ink">{Math.round(validation.confidence)}%</span>
          </div>
          <div className="w-full bg-app-line rounded-full h-2" role="progressbar" aria-label="Evidence confidence" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(validation.confidence)}>
            <div className="bg-app-focus h-2 rounded-full transition" style={{ width: `${validation.confidence}%` }} />
          </div>
        </div>
        {validation.errors.length > 0 && (
          <div className="bg-app-critical/10 border border-app-critical rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-app-critical" />
              <span className="text-sm font-medium text-app-critical">Errors ({validation.errors.length})</span>
            </div>
            <ul className="space-y-1 max-h-32 overflow-y-auto">
              {validation.errors.slice(0, 10).map((e, i) => <li key={i} className="text-sm text-app-critical">{e}</li>)}
              {validation.errors.length > 10 && <li className="text-sm text-app-critical">...and {validation.errors.length - 10} more</li>}
            </ul>
          </div>
        )}
        {validation.warnings.length > 0 && (
          <div className="bg-app-warning/10 border border-app-warning rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-app-warning" />
              <span className="text-sm font-medium text-app-warning">Warnings ({validation.warnings.length})</span>
            </div>
            <ul className="space-y-1 max-h-32 overflow-y-auto">
              {validation.warnings.slice(0, 5).map((w, i) => <li key={i} className="text-sm text-app-warning">{w}</li>)}
            </ul>
          </div>
        )}

        {validation.unvalidatedResearchCount > 0 && (
          <div className="bg-app-warning/10 border border-app-warning rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-app-warning" />
              <span className="text-sm font-medium text-app-warning">
                {validation.unvalidatedResearchCount} researched value{validation.unvalidatedResearchCount > 1 ? 's' : ''} must be reviewed before validation
              </span>
            </div>
          </div>
        )}

        <button
          onClick={onValidate}
          disabled={validating || !validation.isValid}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-app-brand text-white rounded-lg font-medium hover:bg-app-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {validating ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Validating...</>
          ) : !validation.isValid ? (
            <><AlertCircle className="w-5 h-5" /> Resolve issues to validate</>
          ) : (
            <><CheckCircle className="w-5 h-5" /> Validate Configuration</>
          )}
        </button>
      </div>
    </div>
  );
}
